import {
  Animations,
  Button,
  ModalController,
  Spinners,
  useForm,
  useToast,
  Validators,
} from '@keymax-dev/smartepi-ui';
import { motion, useAnimation } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import PageTitle from '../../../components/page-title';
import useRest from '../../../hooks/use-rest';
import { EndPoints } from '../../../utils/constants';
import { getErrorMessage } from '../../../utils/error-messages';
import { AddCostCenterModalElement } from './style';
import { CostCenter, ErrorResponse } from '../../../domain/models';

enum Content {
  Form,
  Loading,
}

interface AddCostCenterModalProps {
  controller?: ModalController;
}

const { usePost } = useRest();

const Strings = {
  Buttons: {
    Add: 'Adicionar',
  },
  Inputs: {
    CostCenterName: 'Nome do Centro de Custo',
  },
  Messages: {
    RegisterLoading: 'Cadastrando Centro de Custo...',
    RegisterSuccess: 'Centro de Custo adicionado com successo!',
    UnknownError:
      'Ocorreu um erro inesperado. Por favor, verifique sua conexão com a internet.',
    InvalidForm: 'Os dados fornecidos são inválidos!',
  },
  Title: 'Adicionar Centro de Custo',
};

export default function AddCostCenterModal({
  controller,
}: AddCostCenterModalProps): JSX.Element {
  const [data, post] = usePost({ loading: false });
  const errorToast = useToast(<span />, { color: 'danger' });
  const successToast = useToast(
    <span>{Strings.Messages.RegisterSuccess}</span>,
    { color: 'success' }
  );

  const [form, getErrors, getValues] = useForm(
    [
      {
        key: 'name',
        validators: [new Validators.Required()],
      },
    ],
    'addCostCenter'
  );

  const contentAnimation = useAnimation();
  const [content, _setContent] = useState<Content>(Content.Form);
  const setContent = (value: Content): void => {
    contentAnimation
      .start(Animations.FadeOut)
      .then(() => Promise.resolve(_setContent(value)))
      .then(() => contentAnimation.start(Animations.FadeIn));
  };

  const requestSuccessHandler = (data: CostCenter.Data): void => {
    setTimeout(() => successToast.open());
    if (controller) controller.close(data);
  };

  const requestErrorHandler = (error: ErrorResponse): void => {
    errorToast.setContent(getErrorMessage(error));
    setTimeout(() => errorToast.open());
  };

  const confirmHandler = (): void => {
    if (!getErrors()) {
      post(EndPoints.CostCenter(), getValues());
    } else {
      errorToast.setContent(Strings.Messages.InvalidForm);
      errorToast.open();
    }
  };

  useEffect(() => {
    if (data.loading) {
      setContent(Content.Loading);
    } else {
      setContent(Content.Form);
      if (data.payload) requestSuccessHandler(data.payload.data);
      else if (data.error) requestErrorHandler(data.error);
    }
  }, [data]);

  return (
    <AddCostCenterModalElement>
      <PageTitle>{Strings.Title}</PageTitle>

      {content === Content.Form && (
        <motion.div className="__form-container">
          <div className="__form-container">
            <form.name placeholder={Strings.Inputs.CostCenterName} />
          </div>
          <Button
            text={Strings.Buttons.Add}
            color="success"
            onClick={confirmHandler}
          />
        </motion.div>
      )}
      {content === Content.Loading && (
        <motion.div
          className="__form-container"
          animate={contentAnimation}
          initial={{ opacity: 0 }}
        >
          <Spinners.circles width="300px" height="300px" />
          <span>{Strings.Messages.RegisterLoading}</span>
        </motion.div>
      )}
    </AddCostCenterModalElement>
  );
}
